import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { ShieldCheck, Receipt, Ban, BadgePercent, Undo2, FileClock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getActiveRestaurantId } from '../utils/roleAccess';
import { SkeletonTable, EmptyState } from '../components/LoadingStates';
import { auditLogService } from '../services/auditLog';
import { formatCurrency, formatDateTime } from './inventory/inventoryUtils';
import { Th } from './inventory/StockItemsTab';

const ACTION_META: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
  SETTLE: { label: 'Settle', cls: 'bg-green-100 text-green-800', icon: <Receipt className="h-3.5 w-3.5" /> },
  VOID: { label: 'Void', cls: 'bg-red-100 text-red-700', icon: <Ban className="h-3.5 w-3.5" /> },
  CANCEL: { label: 'Cancel', cls: 'bg-red-100 text-red-700', icon: <Ban className="h-3.5 w-3.5" /> },
  REFUND: { label: 'Refund', cls: 'bg-orange-100 text-orange-700', icon: <Undo2 className="h-3.5 w-3.5" /> },
  DISCOUNT: { label: 'Discount', cls: 'bg-amber-100 text-amber-800', icon: <BadgePercent className="h-3.5 w-3.5" /> },
  ITEM_REMOVED: { label: 'Item removed', cls: 'bg-gray-200 text-gray-700', icon: <Ban className="h-3.5 w-3.5" /> },
};

const FILTERS = ['', 'SETTLE', 'VOID', 'CANCEL', 'REFUND', 'DISCOUNT'];

export const AuditLogPage: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const org = user?.organizationCode;
  const restaurantId = getActiveRestaurantId() || undefined;
  const [action, setAction] = useState('');

  const { data: logs, isLoading } = useQuery(
    ['audit-logs', org, restaurantId, action],
    () => auditLogService.list(org!, restaurantId, action || undefined),
    { enabled: !!org },
  );

  if (!org) {
    return <div className="p-6"><EmptyState icon={<ShieldCheck className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />} title="No organization selected" description="Your account is not linked to an organization." /></div>;
  }

  const meta = (a: string) => ACTION_META[a] || { label: a, cls: 'bg-gray-100 text-gray-600', icon: <FileClock className="h-3.5 w-3.5" /> };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center gap-3.5 mb-6">
        <div className="grid place-items-center h-12 w-12 rounded-2xl text-white shrink-0" style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 14px 32px -12px ${currentTheme.colors.primary[500]}` }}>
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: currentTheme.colors.text.primary }}>Audit Log</h1>
          <p style={{ color: currentTheme.colors.text.secondary }}>Sensitive actions — settlements, voids, refunds and discounts</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map(a => (
          <button key={a || 'all'} onClick={() => setAction(a)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border"
            style={{
              backgroundColor: action === a ? currentTheme.colors.primary[600] : currentTheme.colors.background.card,
              color: action === a ? '#fff' : currentTheme.colors.text.secondary,
              borderColor: action === a ? currentTheme.colors.primary[600] : currentTheme.colors.border.primary,
            }}>
            {a === '' ? 'All' : meta(a).label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <SkeletonTable rows={8} columns={5} />
      ) : !logs || logs.length === 0 ? (
        <EmptyState icon={<FileClock className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />} title="No audit entries" description="Sensitive actions will be recorded here as they happen." />
      ) : (
        <div className="overflow-x-auto rounded-xl border" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
          <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
            <thead className="ui-thead">
              <tr><Th>When</Th><Th>Action</Th><Th>Reference</Th><Th>Details</Th><Th className="text-right">Amount</Th></tr>
            </thead>
            <tbody className="ui-tbody">
              {logs.map(l => {
                const m = meta(l.action);
                return (
                  <tr key={l.id}>
                    <td className="px-4 py-3 text-sm whitespace-nowrap" style={{ color: currentTheme.colors.text.secondary }}>{formatDateTime(l.createdAt)}</td>
                    <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>{m.icon}{m.label}</span></td>
                    <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>{l.entityLabel || l.entityId || '—'}</td>
                    <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{l.description || '—'}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium" style={{ color: currentTheme.colors.text.primary }}>{l.amount != null ? formatCurrency(l.amount) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
